<template>
    <section class="flex flex-col gap-3">
        <h2 class="body-small-semibold text-valence-stone-900">Category Scores</h2>
        <p v-if="intro" class="body-small text-valence-stone-500">{{ intro }}</p>
        <BaseCard padding="lg" class="flex flex-col gap-5">
            <div v-for="section in visibleSections" :key="section.section_id" class="flex flex-col gap-2.5">
                <div class="flex items-center justify-between body-small text-valence-stone-900">
                    <span>{{ section.title || "Questions" }}</span>
                    <span class="body-small">
                        <span class="body-small-bold">{{ formatAvg(section.avg) }}</span
                        ><span class="text-valence-stone-500">/{{ maxScore }}</span>
                    </span>
                </div>
                <!-- Score bar: cream track, brand-pink fill. Announced to
                     assistive tech via the ARIA attributes below. -->
                <div
                    class="self-score-track"
                    role="progressbar"
                    :aria-valuenow="section.avg ?? 0"
                    :aria-valuemin="0"
                    :aria-valuemax="maxScore"
                    :aria-label="`${section.title || 'Questions'} average score`">
                    <span class="self-score-fill" :style="{ width: `${percent(section.avg)}%` }"></span>
                </div>
            </div>
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import type { SectionAggregate } from "~vue/api/generated/reflections-admin";
import BaseCard from "~vue/components/BaseCard.vue";
import { computed } from "vue";

interface Props {
    sections: SectionAggregate[];
    maxScore: number;
    // Optional descriptive line below the heading. Used on the admin campaign
    // report to explain what the rolled-up bars represent.
    intro?: string;
}

const props = defineProps<Props>();

const visibleSections = computed(() => props.sections.filter((s) => s.avg !== null));

function percent(avg: number | null): number {
    if (avg === null || !props.maxScore) {
        return 0;
    }
    return Math.max(0, Math.min(100, Math.round((avg / props.maxScore) * 100)));
}

function formatAvg(avg: number | null): string {
    if (avg === null) {
        return "—";
    }
    return avg.toFixed(1);
}
</script>

<style scoped>
.self-score-track {
    height: 17px;
    background: #f5f1e9;
    border-radius: 5px;
    padding: 0;
    overflow: hidden;
    display: block;
}

.self-score-fill {
    display: block;
    height: 100%;
    background: #cd2f7b;
    border-radius: 5px;
    transition: width 320ms ease;
}

@media (prefers-reduced-motion: reduce) {
    .self-score-fill {
        transition: none;
    }
}
</style>
